import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, ImagePlus, Droplets, Share2, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const key = (a) => `hv_launch_steps_${a.toLowerCase()}`;
const load = (a) => { try { return JSON.parse(localStorage.getItem(key(a)) || "{}"); } catch { return {}; } };

// Post-launch 1-2-3 checklist: brand → liquidity → share. Completion is detected from real data where possible.
export const LaunchChecklist = ({ token, compact }) => {
  const a = token.address;
  const mainnet = token.chain_id === 4663;
  const [st, setSt] = useState(() => load(a));
  const [pools, setPools] = useState(null);
  const [logo, setLogo] = useState(!!token.logo);
  useEffect(() => { localStorage.setItem(key(a), JSON.stringify(st)); }, [a, st]);
  useEffect(() => {
    if (!mainnet) return;
    api.get(`/liquidity/pools/${a}`).then((r) => setPools(r.data.pools.filter((p) => p.live).length)).catch(() => setPools(0));
    api.get(`/token-page/${a}`).then((r) => setLogo(!!r.data.logo_url)).catch(() => {});
  }, [a, mainnet]);
  const share = () => {
    const url = `${window.location.origin}/token/${a}`;
    (navigator.share ? navigator.share({ title: `${token.symbol} on HIVEVORA`, url }) : navigator.clipboard.writeText(url).then(() => toast.success("Token page link copied")))
      .then(() => setSt((s) => ({ ...s, shared: true }))).catch(() => {});
  };
  const steps = [
    { n: 1, icon: ImagePlus, title: "Brand your token", body: "Add a logo, banner and project colors to your token page.", done: logo, to: `/token/${a}?customize=1`, cta: "Open token page", id: "brand", na: !mainnet && "Token pages are mainnet-only" },
    { n: 2, icon: Droplets, title: "Add liquidity", body: "Create a Uniswap v3 pool so people can trade your token. You sign this separately.", done: pools > 0, to: `/liquidity?tab=create&token=${a}`, cta: "Create pool", id: "liquidity", na: !mainnet && "Uniswap isn't deployed on testnet" },
    { n: 3, icon: Share2, title: "Share your token", body: "Share the token page link with your community.", done: !!st.shared, onClick: share, cta: "Share / copy link", id: "share" },
  ];
  const open = steps.filter((s) => !s.done && !s.na && !st[`skip_${s.id}`]);
  if (!open.length && compact) return null;
  return (
    <div className="rounded-xl border border-lime/25 bg-lime/[0.04] p-4" data-testid={`launch-checklist-${a}`}>
      <p className="text-xs font-bold uppercase tracking-widest text-lime mb-3">{open.length ? `Finish your launch: ${3 - open.length}/3 done` : "Launch checklist complete"}</p>
      <ol className="grid md:grid-cols-3 gap-3">
        {steps.map((s) => {
          const skipped = st[`skip_${s.id}`];
          const Done = s.done ? CheckCircle2 : Circle;
          return (
            <li key={s.n} data-testid={`launch-step-${s.id}`} data-state={s.done ? "done" : s.na ? "na" : skipped ? "skipped" : "todo"} className={`rounded-lg border p-3 ${s.done ? "border-lime/40" : "border-white/10"} ${skipped || s.na ? "opacity-50" : ""}`}>
              <p className="flex items-center gap-2 text-sm font-semibold text-white"><span className="grid h-6 w-6 place-items-center rounded-full bg-lime text-black text-xs font-black">{s.n}</span><s.icon className="h-4 w-4 text-lime" />{s.title}<Done className={`ml-auto h-4 w-4 ${s.done ? "text-lime" : "text-slate-600"}`} /></p>
              <p className="mt-1 text-[11px] text-slate-400">{s.na || s.body}</p>
              {!s.done && !s.na && <div className="mt-2 flex gap-2">
                {s.to ? <Link data-testid={`launch-step-${s.id}-go`} to={s.to} className="btn-lime px-3 py-1 text-[11px]">{s.cta}</Link>
                  : <button data-testid={`launch-step-${s.id}-go`} onClick={s.onClick} className="btn-lime px-3 py-1 text-[11px]">{s.cta}</button>}
                <button data-testid={`launch-step-${s.id}-skip`} onClick={() => setSt((x) => ({ ...x, [`skip_${s.id}`]: !skipped }))} className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-white"><SkipForward className="h-3 w-3" />{skipped ? "Undo skip" : "Skip"}</button>
              </div>}
            </li>
          );
        })}
      </ol>
    </div>
  );
};
